/* global globalThis */

import { useEffect, useRef } from 'react'

const DEFAULT_INITIAL_CHECK_DELAYS = [1000]
const DEFAULT_BOTTOM_OFFSET = 400
const DEFAULT_COOLDOWN_MS = 1000

const getWindow = () => globalThis.window

const getDocument = () => globalThis.document

const getScrollTop = (win = getWindow(), doc = getDocument()) =>
  win?.pageYOffset ||
  doc?.documentElement?.scrollTop ||
  doc?.body?.scrollTop ||
  0

const getScrollHeight = (doc = getDocument()) =>
  doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight || 0

const getClientHeight = (win = getWindow(), doc = getDocument()) =>
  win?.innerHeight || doc?.documentElement?.clientHeight || 0

const scheduleAnimationFrame = (callback) =>
  typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame(callback)
    : setTimeout(callback, 16)

const clearProcessingState = (state) => {
  state.isProcessing = false
}

const scheduleProcessingReset = (state, cooldownMs) => {
  state.resetProcessingTimer = setTimeout(
    clearProcessingState,
    cooldownMs,
    state
  )
}

const endScrollFrame = (state) => {
  state.ticking = false
}

const runScrollFrame = (handleScroll, state) => {
  handleScroll()
  endScrollFrame(state)
}

const shouldSkipLoadMore = ({
  currentPagination,
  isProcessing,
  lastLoadTime,
  cooldownMs,
  now,
}) =>
  !currentPagination ||
  isProcessing ||
  currentPagination.loadingMore ||
  !currentPagination.hasMore ||
  now - lastLoadTime < cooldownMs

const isNearBottom = ({ bottomOffset, win, doc }) =>
  getScrollTop(win, doc) + getClientHeight(win, doc) >=
  getScrollHeight(doc) - bottomOffset

const createThrottledScrollHandler = (handleScroll, state) => () => {
  if (state.ticking) {
    return
  }

  state.ticking = true
  scheduleAnimationFrame(runScrollFrame.bind(null, handleScroll, state))
}

const createHandleScroll =
  ({ callbacksRef, state, cooldownMs, bottomOffset, win, doc }) =>
  () => {
    const {
      getPagination: getCurrentPagination,
      getContentLength: getCurrentContentLength,
      loadMoreData: loadNextPage,
    } = callbacksRef.current

    if (!getCurrentPagination || !getCurrentContentLength || !loadNextPage) {
      return
    }

    const now = Date.now()
    const currentPagination = getCurrentPagination()

    if (
      shouldSkipLoadMore({
        currentPagination,
        isProcessing: state.isProcessing,
        lastLoadTime: state.lastLoadTime,
        cooldownMs,
        now,
      })
    ) {
      return
    }

    if (getCurrentContentLength() === 0) {
      return
    }

    if (!isNearBottom({ bottomOffset, win, doc })) {
      return
    }

    state.isProcessing = true
    state.lastLoadTime = now

    Promise.resolve(loadNextPage()).then(
      scheduleProcessingReset.bind(null, state, cooldownMs),
      scheduleProcessingReset.bind(null, state, cooldownMs)
    )
  }

export const useInfiniteScroll = ({
  enabled = true,
  getPagination,
  getContentLength,
  loadMoreData,
  initialCheckDelays = DEFAULT_INITIAL_CHECK_DELAYS,
  bottomOffset = DEFAULT_BOTTOM_OFFSET,
  cooldownMs = DEFAULT_COOLDOWN_MS,
}) => {
  const callbacksRef = useRef({
    getPagination,
    getContentLength,
    loadMoreData,
  })

  useEffect(() => {
    callbacksRef.current = {
      getPagination,
      getContentLength,
      loadMoreData,
    }
  }, [getPagination, getContentLength, loadMoreData])

  useEffect(() => {
    const win = getWindow()
    const doc = getDocument()

    if (!enabled || !win || !doc) {
      return undefined
    }

    const state = {
      ticking: false,
      isProcessing: false,
      lastLoadTime: 0,
      resetProcessingTimer: null,
    }

    const handleScroll = createHandleScroll({
      callbacksRef,
      state,
      cooldownMs,
      bottomOffset,
      win,
      doc,
    })

    const throttledHandleScroll = createThrottledScrollHandler(
      handleScroll,
      state
    )

    win.addEventListener('scroll', throttledHandleScroll, { passive: true })
    doc.addEventListener('scroll', throttledHandleScroll, { passive: true })

    const timers = initialCheckDelays.map((delay) =>
      setTimeout(handleScroll, delay)
    )

    return () => {
      timers.forEach(clearTimeout)

      if (state.resetProcessingTimer) {
        clearTimeout(state.resetProcessingTimer)
      }

      win.removeEventListener('scroll', throttledHandleScroll)
      doc.removeEventListener('scroll', throttledHandleScroll)
    }
  }, [enabled, cooldownMs, bottomOffset, initialCheckDelays.join(',')])
}
